

import type { User, AvailabilityRequest, Thread, Message } from '@/types';
import { addDays, setHours, startOfDay, subMinutes } from 'date-fns';

export const MOCK_DETAILED_EMPLOYEES: User[] = [
  {
    id: 'emp1',
    name: 'Alice Smith',
    email: 'alice@example.com',
    role: 'employee',
    avatarUrl: 'https://placehold.co/100x100.png?text=AS',
    status: 'online',
    phoneNumber: '+15551234567',
    startDate: '2023-01-15T00:00:00.000Z',
    currentWage: 18.50,
    skills: ['Latte Art', 'Customer Service', 'POS Operation'],
    certifications: [
      { id: 'cert1_alice', name: 'Barista Level 1', issuingOrg: 'Coffee Guild', issueDate: '2023-02-20T00:00:00.000Z', expiryDate: '2025-02-20T00:00:00.000Z' },
      { id: 'cert2_alice', name: 'Food Safety Handling', issueDate: '2023-01-30T00:00:00.000Z' },
    ],
    wageHistory: [
      { id: 'wage1_alice', date: '2023-01-15T00:00:00.000Z', newWage: 16.00, newTitle: 'Junior Barista' },
      { id: 'wage2_alice', date: '2023-07-15T00:00:00.000Z', newWage: 17.50, newTitle: 'Barista' },
      { id: 'wage3_alice', date: '2024-01-15T00:00:00.000Z', newWage: 18.50, notes: 'Annual review increase.' },
    ],
    recurringPreferences: [
        { id: 'rp1_alice', dayOfWeek: 'Monday', startTime: '09:00', endTime: '17:00' },
        { id: 'rp2_alice', dayOfWeek: 'Wednesday', startTime: '12:00', endTime: '18:00' },
    ]
  },
  {
    id: 'emp2',
    name: 'Bob Johnson',
    email: 'bob@example.com',
    role: 'employee',
    status: 'offline',
    avatarUrl: 'https://placehold.co/100x100.png?text=BJ',
    phoneNumber: '+15559876543',
    startDate: '2022-11-01T00:00:00.000Z',
    currentWage: 20.00,
    skills: ['Inventory Management', 'Shift Closing', 'Espresso Machine Maintenance'],
    certifications: [
      { id: 'cert1_bob', name: 'Advanced Barista', issuingOrg: 'Specialty Coffee Assoc.', issueDate: '2023-03-10T00:00:00.000Z' },
    ],
    wageHistory: [
      { id: 'wage1_bob', date: '2022-11-01T00:00:00.000Z', newWage: 18.00, newTitle: 'Barista' },
      { id: 'wage2_bob', date: '2023-05-01T00:00:00.000Z', newWage: 20.00, newTitle: 'Senior Barista', notes: 'Promotion to Senior Barista' },
    ],
    recurringPreferences: [
       { id: 'rp1_bob', dayOfWeek: 'Tuesday', startTime: '14:00', endTime: '18:00' },
       { id: 'rp2_bob', dayOfWeek: 'Thursday', startTime: '14:00', endTime: '18:00' },
       { id: 'rp3_bob', dayOfWeek: 'Friday', startTime: '10:00', endTime: '16:00' },
    ]
  },
  {
    id: 'emp3',
    name: 'Charlie Brown',
    email: 'charlie@example.com',
    role: 'employee',
    status: 'busy',
    avatarUrl: 'https://placehold.co/100x100.png?text=CB',
    phoneNumber: '+15551112222',
    startDate: '2024-03-01T00:00:00.000Z',
    currentWage: 17.00,
    skills: ['Cash Handling', 'Friendly Service'],
    certifications: [],
    wageHistory: [
      { id: 'wage1_charlie', date: '2024-03-01T00:00:00.000Z', newWage: 17.00, newTitle: 'Trainee Barista' },
    ],
    recurringPreferences: []
  },
  {
    id: 'manager1', // Mock manager user
    name: 'Manager Mike',
    email: 'manager@example.com',
    role: 'manager',
    status: 'online',
    avatarUrl: 'https://placehold.co/100x100.png?text=MM',
    phoneNumber: '+15553334444',
    startDate: '2021-01-01T00:00:00.000Z',
    currentWage: 30.00, // Managers might have wages or salaries
    skills: ['Team Leadership', 'Scheduling', 'Conflict Resolution', 'Budgeting'],
    certifications: [
      { id: 'cert1_manager', name: 'Management Essentials', issuingOrg: 'Business Institute', issueDate: '2022-05-10T00:00:00.000Z' },
    ],
    wageHistory: [
      { id: 'wage1_manager', date: '2021-01-01T00:00:00.000Z', newWage: 25.00, newTitle: 'Assistant Manager' },
      { id: 'wage2_manager', date: '2022-01-01T00:00:00.000Z', newWage: 30.00, newTitle: 'Cafe Manager', notes: 'Promotion' },
    ],
    recurringPreferences: [] // Managers typically set their own schedules or have fixed ones
  }
];

// This simple list is used by other pages for quick employee selection
export const mockSimpleEmployees = MOCK_DETAILED_EMPLOYEES.filter(u => u.role === 'employee').map(user => ({
  id: user.id,
  name: user.name,
  role: user.role,
}));


const today = startOfDay(new Date());

export const MOCK_AVAILABILITY_REQUESTS: AvailabilityRequest[] = [
    {
        id: 'req1',
        userId: 'emp1',
        userName: 'Alice Smith',
        type: 'unavailability',
        startDate: setHours(addDays(today, 2), 9), // two days from now, 9 AM
        endDate: setHours(addDays(today, 2), 12),   // two days from now, 12 PM
        isAllDay: false,
        reason: "Doctor's Appointment",
        status: 'approved',
    },
    {
        id: 'req2',
        userId: 'emp2',
        userName: 'Bob Johnson',
        type: 'unavailability',
        startDate: addDays(today, 3), // three days from now
        endDate: addDays(today, 3),
        isAllDay: true,
        reason: 'Family event',
        status: 'pending',
    },
    {
        id: 'req3',
        userId: 'emp3',
        userName: 'Charlie Brown',
        type: 'vacation',
        startDate: addDays(today, 10), // 10 days from now
        endDate: addDays(today, 14),   // 14 days from now
        isAllDay: true,
        reason: 'Short trip',
        status: 'approved',
    },
    {
        id: 'req4',
        userId: 'emp1',
        userName: 'Alice Smith',
        type: 'vacation',
        startDate: addDays(today, 30),
        endDate: addDays(today, 40),
        isAllDay: true,
        status: 'pending',
    },
     {
        id: 'req5',
        userId: 'emp2',
        userName: 'Bob Johnson',
        type: 'unavailability',
        startDate: setHours(addDays(today, 5), 14), // 5 days from now, 2 PM
        endDate: setHours(addDays(today, 5), 18),   // 5 days from now, 6 PM
        isAllDay: false,
        reason: 'University class',
        status: 'approved',
    },
];


// --- MOCK MESSAGING DATA ---
const manager = MOCK_DETAILED_EMPLOYEES.find(u => u.role === 'manager')!;
const alice = MOCK_DETAILED_EMPLOYEES.find(u => u.email === 'alice@example.com')!;
const bob = MOCK_DETAILED_EMPLOYEES.find(u => u.email === 'bob@example.com')!;

export const MOCK_THREADS: Thread[] = [
  {
    id: 'thread1',
    type: 'direct',
    participantIds: [manager.id, alice.id].sort(),
    participants: [manager, alice],
    lastMessage: { content: 'Sure, I can take a look at it.', timestamp: subMinutes(new Date(), 10) },
    unreadCounts: { [manager.id]: 2 },
    createdAt: subMinutes(new Date(), 30),
    updatedAt: subMinutes(new Date(), 10),
  },
  {
    id: 'thread2',
    type: 'direct',
    participantIds: [manager.id, bob.id].sort(),
    participants: [manager, bob],
    lastMessage: { content: 'Okay, sounds good!', timestamp: subMinutes(new Date(), 120) },
    unreadCounts: {},
    createdAt: subMinutes(new Date(), 150),
    updatedAt: subMinutes(new Date(), 120),
  },
  {
    id: 'thread-channel-1',
    type: 'channel',
    name: '#Announcements',
    participantIds: MOCK_DETAILED_EMPLOYEES.map(u => u.id),
    lastMessage: { content: 'Health inspector is arriving in 1 hour.', timestamp: subMinutes(new Date(), 60) },
    unreadCounts: { [alice.id]: 1, [bob.id]: 1 },
    createdAt: subMinutes(new Date(), 180),
    updatedAt: subMinutes(new Date(), 5),
  },
  {
    id: 'thread-channel-2',
    type: 'channel',
    name: '#General',
    participantIds: MOCK_DETAILED_EMPLOYEES.map(u => u.id),
    lastMessage: { content: 'Who left the milk out again?', timestamp: subMinutes(new Date(), 15) },
    unreadCounts: {},
    createdAt: subMinutes(new Date(), 200),
    updatedAt: subMinutes(new Date(), 15),
  },
];

export const MOCK_MESSAGES: Message[] = [
  // Thread 1: Manager and Alice
  { id: 'msg1', threadId: 'thread1', senderId: alice.id, content: 'Hey Mike, can you check the schedule for next week?', createdAt: subMinutes(new Date(), 12), type: 'text', readBy: [alice.id, manager.id] },
  { id: 'msg2', threadId: 'thread1', senderId: alice.id, content: 'I think there might be a mistake in my hours.', createdAt: subMinutes(new Date(), 11), type: 'text', readBy: [alice.id, manager.id] },
  { id: 'msg3', threadId: 'thread1', senderId: manager.id, content: 'Sure, I can take a look at it.', createdAt: subMinutes(new Date(), 10), type: 'text', readBy: [manager.id, alice.id] },

  // Thread 2: Manager and Bob
  { id: 'msg4', threadId: 'thread2', senderId: bob.id, content: 'I finished the inventory count.', createdAt: subMinutes(new Date(), 125), type: 'text', readBy: [bob.id, manager.id] },
  { id: 'msg5', threadId: 'thread2', senderId: manager.id, content: 'Okay, sounds good!', createdAt: subMinutes(new Date(), 120), type: 'text', readBy: [bob.id, manager.id] },

  // Channel: Announcements
  { id: 'msg6', threadId: 'thread-channel-1', senderId: manager.id, content: 'Reminder: Staff meeting tomorrow at 8 AM sharp.', createdAt: subMinutes(new Date(), 150), type: 'announcement', readBy: [manager.id] },
  { id: 'msg7', threadId: 'thread-channel-1', senderId: manager.id, content: 'Health inspector is arriving in 1 hour.', createdAt: subMinutes(new Date(), 60), type: 'announcement', readBy: [manager.id] },

  // Channel: General
  { id: 'msg8', threadId: 'thread-channel-2', senderId: bob.id, content: 'Who left the milk out again?', createdAt: subMinutes(new Date(), 15), type: 'text', readBy: [bob.id] },
];

    